/**
 * An indexed, read-only view of a `Curriculum`.
 *
 * Content is authored as plain data (see `content/`); the registry is what the
 * application actually queries. Building it is O(n) and happens once at module
 * load, so every lookup in the UI is a map hit rather than an array scan.
 */

import { buildGraph, type ConceptGraph } from './graph.ts';
import type { Concept, Course, Curriculum, Lesson, Level, LevelId, Strand, StrandId, Unit } from './types.ts';

export class CurriculumRegistry {
  readonly graph: ConceptGraph;

  private readonly conceptById = new Map<string, Concept>();
  private readonly courseById = new Map<string, Course>();
  private readonly unitById = new Map<string, Unit>();
  private readonly lessonById = new Map<string, Lesson>();
  private readonly levelById = new Map<LevelId, Level>();
  private readonly strandById = new Map<StrandId, Strand>();
  private readonly lessonsByConcept = new Map<string, Lesson[]>();

  constructor(readonly curriculum: Curriculum) {
    for (const concept of curriculum.concepts) this.conceptById.set(concept.id, concept);
    for (const course of curriculum.courses) this.courseById.set(course.id, course);
    for (const unit of curriculum.units) this.unitById.set(unit.id, unit);
    for (const level of curriculum.levels) this.levelById.set(level.id, level);
    for (const strand of curriculum.strands) this.strandById.set(strand.id, strand);
    for (const lesson of curriculum.lessons) {
      this.lessonById.set(lesson.id, lesson);
      const bucket = this.lessonsByConcept.get(lesson.conceptId) ?? [];
      bucket.push(lesson);
      this.lessonsByConcept.set(lesson.conceptId, bucket);
    }
    this.graph = buildGraph(curriculum.concepts);
  }

  get concepts(): Concept[] {
    return this.curriculum.concepts;
  }
  get courses(): Course[] {
    return this.curriculum.courses;
  }
  get levels(): Level[] {
    return [...this.curriculum.levels].sort((a, b) => a.order - b.order);
  }
  get strands(): Strand[] {
    return [...this.curriculum.strands].sort((a, b) => a.order - b.order);
  }

  concept(id: string): Concept | undefined {
    return this.conceptById.get(id);
  }
  course(id: string): Course | undefined {
    return this.courseById.get(id);
  }
  unit(id: string): Unit | undefined {
    return this.unitById.get(id);
  }
  lesson(id: string): Lesson | undefined {
    return this.lessonById.get(id);
  }
  level(id: LevelId): Level | undefined {
    return this.levelById.get(id);
  }
  strand(id: StrandId): Strand | undefined {
    return this.strandById.get(id);
  }

  /** Concepts with lessons and exercises behind them today. */
  availableConcepts(): Concept[] {
    return this.concepts.filter((c) => c.status === 'available');
  }

  lessonsForConcept(conceptId: string): Lesson[] {
    return this.lessonsByConcept.get(conceptId) ?? [];
  }

  unitsOfCourse(courseId: string): Unit[] {
    const course = this.course(courseId);
    if (!course) return [];
    return course.unitIds.map((id) => this.unit(id)).filter((u): u is Unit => u !== undefined);
  }

  lessonsOfUnit(unitId: string): Lesson[] {
    const unit = this.unit(unitId);
    if (!unit) return [];
    return unit.lessonIds.map((id) => this.lesson(id)).filter((l): l is Lesson => l !== undefined);
  }

  lessonsOfCourse(courseId: string): Lesson[] {
    return this.unitsOfCourse(courseId).flatMap((unit) => this.lessonsOfUnit(unit.id));
  }

  conceptsOfCourse(courseId: string): Concept[] {
    const seen = new Set<string>();
    const out: Concept[] = [];
    for (const lesson of this.lessonsOfCourse(courseId)) {
      if (seen.has(lesson.conceptId)) continue;
      seen.add(lesson.conceptId);
      const concept = this.concept(lesson.conceptId);
      if (concept) out.push(concept);
    }
    return out;
  }

  courseOfLesson(lessonId: string): Course | undefined {
    const lesson = this.lesson(lessonId);
    if (!lesson) return undefined;
    const unit = this.unit(lesson.unitId);
    return unit ? this.course(unit.courseId) : undefined;
  }

  coursesForLevel(level: LevelId): Course[] {
    return this.courses.filter((course) => course.level === level);
  }

  conceptsInStrand(strand: StrandId): Concept[] {
    return this.concepts.filter((concept) => concept.strand === strand);
  }

  /**
   * Ordered position of a lesson inside its course, so the lesson page can
   * offer "next" without the caller re-deriving course structure.
   */
  neighbouringLessons(lessonId: string): { previous?: Lesson; next?: Lesson } {
    const course = this.courseOfLesson(lessonId);
    if (!course) return {};
    const lessons = this.lessonsOfCourse(course.id);
    const index = lessons.findIndex((lesson) => lesson.id === lessonId);
    if (index === -1) return {};
    return { previous: lessons[index - 1], next: lessons[index + 1] };
  }
}
